// frontend/src/context/MessagingContext.jsx
// Updated: January 3, 2026 - Cycle 5 Phase 3
// Connected to GraphQL backend instead of local state
// ========================================================

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import { useAuth } from './AuthContext';

// GraphQL Queries
const GET_MY_CONVERSATIONS = gql`
  query GetMyConversations($limit: Int) {
    myConversations(limit: $limit) {
      id
      conversationId
      type
      participants
      lastMessagePreview
      lastMessageTime
      unreadMessages
      metadata {
        title
        description
        isArchived
        isPinned
      }
    }
  }
`;

const GET_CONVERSATION_MESSAGES = gql`
  query GetConversationMessages($conversationId: String!, $limit: Int, $before: String) {
    conversationMessages(conversationId: $conversationId, limit: $limit, before: $before) {
      id
      conversationId
      sender
      recipients
      messageType
      text
      imageUrl
      isRead
      timestamp
      createdAt
    }
  }
`;

const GET_DIRECT_CONVERSATION = gql`
  query GetDirectConversation($recipientId: String!) {
    directConversation(recipientId: $recipientId) {
      id
      conversationId
      type
      participants
      lastMessagePreview
      lastMessageTime
      unreadMessages
    }
  }
`;

const GET_UNREAD_COUNT = gql`
  query GetUnreadMessageCount {
    unreadMessageCount
  }
`;

// GraphQL Mutations
const SEND_MESSAGE = gql`
  mutation SendMessage($conversationId: String!, $content: String!, $messageType: String) {
    sendMessage(conversationId: $conversationId, content: $content, messageType: $messageType) {
      success
      message {
        id
        conversationId
        sender
        text
        timestamp
      }
      error
    }
  }
`;

const START_DIRECT_CONVERSATION = gql`
  mutation StartDirectConversation($recipientId: String!, $initialMessage: String) {
    startDirectConversation(recipientId: $recipientId, initialMessage: $initialMessage) {
      success
      conversation {
        id
        conversationId
        type
        participants
      }
      error
    }
  }
`;

const MARK_CONVERSATION_READ = gql`
  mutation MarkConversationRead($conversationId: String!) {
    markConversationRead(conversationId: $conversationId) {
      success
      message
    }
  }
`;

const ARCHIVE_CONVERSATION = gql`
  mutation ArchiveConversation($conversationId: String!) {
    archiveConversation(conversationId: $conversationId) {
      success
      message
    }
  }
`;

const MessagingContext = createContext(null);

export const useMessaging = () => {
  const context = useContext(MessagingContext);
  if (!context) {
    throw new Error('useMessaging must be used within a MessagingProvider');
  }
  return context;
};

export const MessagingProvider = ({ children }) => {
  const { getActiveUser, vendorAuth, memberAuth } = useAuth();
  const [activeThread, setActiveThread] = useState(null);
  const [activeMessages, setActiveMessages] = useState([]);
  const [participantNames, setParticipantNames] = useState({});

  // Get current user
  let currentUser = getActiveUser();
  if (!currentUser && window.location.pathname.startsWith('/vendor')) {
    currentUser = vendorAuth;
  }

  // Queries
  const { 
    data: conversationsData, 
    loading: conversationsLoading, 
    refetch: refetchConversations 
  } = useQuery(GET_MY_CONVERSATIONS, {
    variables: { limit: 50 },
    skip: !currentUser,
    fetchPolicy: 'cache-and-network'
  });

  const { 
    data: unreadData, 
    refetch: refetchUnread 
  } = useQuery(GET_UNREAD_COUNT, {
    skip: !currentUser,
    pollInterval: 30000 // Poll every 30 seconds
  });

  const [getMessages, { loading: messagesLoading }] = useLazyQuery(GET_CONVERSATION_MESSAGES, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data?.conversationMessages) {
        setActiveMessages(data.conversationMessages);
      }
    }
  });

  const [getDirectConversation] = useLazyQuery(GET_DIRECT_CONVERSATION);

  // Mutations
  const [sendMessageMutation] = useMutation(SEND_MESSAGE, {
    onCompleted: (data) => {
      if (data?.sendMessage?.success && data.sendMessage.message) {
        // Add message to active messages
        setActiveMessages(prev => [...prev, data.sendMessage.message]);
        // Refetch conversations to update last message
        refetchConversations();
      }
    }
  });

  const [startConversationMutation] = useMutation(START_DIRECT_CONVERSATION);
  const [markReadMutation] = useMutation(MARK_CONVERSATION_READ);
  const [archiveMutation] = useMutation(ARCHIVE_CONVERSATION);

  // Transform conversations to thread format for frontend compatibility
  const threads = (conversationsData?.myConversations || []).map(conv => ({
    id: conv.conversationId,
    conversationId: conv.conversationId,
    participants: conv.participants,
    participantNames: participantNames,
    participantTypes: {}, // Could be enhanced with user type lookup
    orderRef: null,
    messages: [],
    lastMessage: conv.lastMessagePreview ? {
      content: conv.lastMessagePreview,
      timestamp: conv.lastMessageTime
    } : null,
    lastUpdated: conv.lastMessageTime,
    unreadCount: conv.unreadMessages || 0,
    type: conv.type,
    metadata: conv.metadata
  }));

  const unreadCount = unreadData?.unreadMessageCount || 0;
  const loading = conversationsLoading || messagesLoading;

  // Get or create a thread/conversation
  const getOrCreateThread = useCallback(async (recipientId, recipientName, recipientType, orderRef = null) => {
    if (!currentUser) return null;

    // Store participant name
    setParticipantNames(prev => ({
      ...prev,
      [recipientId]: recipientName,
      [currentUser.userId || currentUser.id]: currentUser.businessName || currentUser.displayName || 'You'
    }));

    try {
      // Check if conversation exists
      const { data } = await getDirectConversation({ 
        variables: { recipientId } 
      });

      if (data?.directConversation) {
        const conv = data.directConversation;
        return {
          id: conv.conversationId,
          conversationId: conv.conversationId,
          participants: conv.participants,
          participantNames: {
            [recipientId]: recipientName,
            [currentUser.userId || currentUser.id]: currentUser.businessName || currentUser.displayName || 'You'
          },
          participantTypes: {
            [recipientId]: recipientType,
            [currentUser.userId || currentUser.id]: currentUser.userType || 'member'
          },
          orderRef,
          messages: [],
          lastMessage: null,
          lastUpdated: new Date().toISOString(),
          unreadCount: conv.unreadMessages || 0
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error getting/creating thread:', error);
      return null;
    }
  }, [currentUser, getDirectConversation]);

  // Send a message
  const sendMessage = useCallback(async (threadId, content) => {
    if (!content.trim() || !threadId) return;

    try {
      await sendMessageMutation({
        variables: {
          conversationId: threadId,
          content: content.trim(),
          messageType: 'text'
        }
      });
    } catch (error) {
      console.error('Error sending message:', error);
    }
  }, [sendMessageMutation]);

  // Start a new conversation with initial message
  const startConversation = useCallback(async (recipientId, initialMessage = null) => {
    try {
      const { data } = await startConversationMutation({
        variables: {
          recipientId,
          initialMessage
        }
      });

      if (data?.startDirectConversation?.success) {
        refetchConversations();
        return data.startDirectConversation.conversation;
      }
      return null;
    } catch (error) {
      console.error('Error starting conversation:', error);
      return null;
    }
  }, [startConversationMutation, refetchConversations]);

  // Mark thread as read
  const markThreadAsRead = useCallback(async (threadId) => {
    try {
      await markReadMutation({
        variables: { conversationId: threadId }
      });
      refetchUnread();
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  }, [markReadMutation, refetchUnread]);

  // Select a thread and load its messages
  const selectThread = useCallback(async (threadId) => {
    const thread = threads.find(t => t.id === threadId || t.conversationId === threadId);
    
    if (thread) {
      setActiveThread(thread);
      
      // Load messages for this conversation
      await getMessages({
        variables: {
          conversationId: threadId,
          limit: 50
        }
      });

      // Mark as read
      markThreadAsRead(threadId);
    }
  }, [threads, getMessages, markThreadAsRead]);

  // Archive a conversation
  const archiveThread = useCallback(async (threadId) => {
    try {
      const { data } = await archiveMutation({
        variables: { conversationId: threadId }
      });

      if (data?.archiveConversation?.success) {
        refetchConversations();
        if (activeThread?.id === threadId) {
          setActiveThread(null);
          setActiveMessages([]);
        }
      }
    } catch (error) {
      console.error('Error archiving conversation:', error);
    }
  }, [archiveMutation, refetchConversations, activeThread]);

  // Quick replies for vendors
  const quickReplies = {
    vendor: {
      orderConfirmed: "Your order has been confirmed!",
      orderDelayed: "We're experiencing a delay with your order. We'll update you soon.",
      orderReady: "Your order is ready for pickup!",
      thankYou: "Thank you for your business!",
      question: "Let me check on that and get back to you."
    },
    member: {
      thanks: "Thank you!",
      question: "I have a question about my order.",
      status: "What's the status of my order?",
      help: "I need help with something."
    }
  };

  // Build active thread with messages
  const activeThreadWithMessages = activeThread ? {
    ...activeThread,
    messages: activeMessages.map(msg => ({
      id: msg.id,
      threadId: msg.conversationId,
      senderId: msg.sender,
      senderName: participantNames[msg.sender] || msg.sender,
      senderType: msg.sender === (currentUser?.userId || currentUser?.id) ? (currentUser?.userType || 'member') : 'other',
      content: msg.text,
      timestamp: msg.timestamp || msg.createdAt,
      read: msg.isRead
    }))
  } : null;

  const value = {
    threads,
    activeThread: activeThreadWithMessages,
    unreadCount,
    loading,
    getOrCreateThread,
    sendMessage,
    startConversation,
    markThreadAsRead,
    selectThread,
    archiveThread,
    quickReplies,
    refetchConversations
  };

  return (
    <MessagingContext.Provider value={value}>
      {children}
    </MessagingContext.Provider>
  );
};

export default MessagingContext;